/**
 * BAB MTD Line Account Modal — account x month breakdown of one Sum line.
 *
 * Inline-Tailwind port of bab-process-app-v2's BabMtdLineDetailModal, so both apps show the
 * same matrix from the same freeze tables.
 *
 * Every cell is a difference of frozen YTD snapshots
 * (AccountFreezeYTD(m) minus the nearest earlier frozen month of the same year), which makes
 * the Total row of a column exactly the line's MTD value in the grid. A month without any
 * account freeze shows a dash — it is never back-filled from live data, and the next frozen
 * month visibly carries the combined delta.
 *
 * There is NO booking level under this matrix on purpose: individual postings only exist in
 * the live ActualTransaction table and cannot reconcile with frozen monthly values. The YTD
 * drill-down keeps its bookings.
 *
 * Amounts are ALWAYS full EUR here — the report's thousands toggle stops at the overview.
 */
import { useState, useEffect, useMemo } from 'react';
import { X, Loader2, Info, Lock, MessageSquare, HelpCircle } from 'lucide-react';
import { babReportApi, ALL_BUSINESS_UNITS } from '../../services/babReportApi';
import { useTopmostEscape } from '../../hooks/useTopmostEscape';
import { formatCompanyLabel } from '../../utils/buTheme';
import { formatEur, applySign } from '../../utils/babFormat';
import { SortableHeader, nextSort, type SortState } from '../ui/SortableHeader';
import { BabAccountTransactionModal } from './BabAccountTransactionModal';
import type {
    BabLineDefinition, BabMtdAccountMatrix, BabMtdAccountRow, BabCommentTarget, SignMode,
    BabCommentCount, BabQueryCount,
} from '../../types/babReport';

type SortKey = 'company' | 'account';

function monthLabel(year: number, month: number): string {
    return `${year}-${String(month).padStart(2, '0')}`;
}

interface Props {
    line: BabLineDefinition;
    year: number;
    month: number;
    months: number;
    apiFilters: { babCompanyCode?: string; buCode?: string; sourceCompany?: string };
    /** Follows the grid's sign toggle — see BabLineAccountModal. */
    signMode: SignMode;
    /** The month column that was clicked — highlighted so the user finds it again. */
    highlight: { year: number; month: number };
    /** Per source company: may this user open the booking level? Real gate is server-side. */
    canDrillCompany: (companyCode: string) => boolean;
    commentedTxRefs: Set<string>;
    onCommentClick: (target: BabCommentTarget) => void;
    queriedTxRefs: Set<string>;
    onQueryClick: (target: BabCommentTarget) => void;
    /** Notes per account AND month, keyed `company:account|year-month`. */
    monthAccountCommentCounts: Record<string, BabCommentCount>;
    monthAccountQueryCounts: Record<string, BabQueryCount>;
    /** Build the anchor for a note on one account in one month. */
    accountMonthTarget: (
        a: { companyCode: string; accountCode: string; accountName: string },
        year: number, month: number
    ) => BabCommentTarget;
    onClose: () => void;
}

export function BabMtdLineAccountModal({
    line, year, month, months, apiFilters, signMode, highlight, canDrillCompany,
    commentedTxRefs, onCommentClick, queriedTxRefs, onQueryClick,
    monthAccountCommentCounts, monthAccountQueryCounts, accountMonthTarget, onClose,
}: Props) {
    const [matrix, setMatrix] = useState<BabMtdAccountMatrix | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sort, setSort] = useState<SortState<SortKey>>({ key: null, direction: 'asc' });
    const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(new Set());
    const [drillAccount, setDrillAccount] = useState<BabMtdAccountRow | null>(null);

    // Only while no booking modal sits on top of this one.
    useTopmostEscape(!drillAccount, onClose);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);
        babReportApi.getLineMtdAccounts(year, month, months, line.lineCode, apiFilters)
            .then(data => { if (active) setMatrix(data); })
            .catch(err => { if (active) setError(err instanceof Error ? err.message : 'Failed to load accounts'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [line.lineCode, year, month, months, apiFilters]);

    const companies = useMemo(() => {
        const set = new Set<string>();
        for (const a of matrix?.accounts ?? []) set.add(a.companyCode);
        return Array.from(set).sort();
    }, [matrix]);

    // Reset the company filter whenever a new account set arrives.
    useEffect(() => { setSelectedCompanies(new Set(companies)); }, [companies]);

    const filtered = useMemo(
        () => (matrix?.accounts ?? []).filter(a => selectedCompanies.has(a.companyCode)),
        [matrix, selectedCompanies]
    );

    const sorted = useMemo(() => {
        if (!sort.key) return filtered;
        const key = sort.key;
        const dir = sort.direction === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => {
            const cmp = key === 'company'
                ? a.companyCode.localeCompare(b.companyCode)
                : a.accountCode.localeCompare(b.accountCode, undefined, { numeric: true });
            return cmp * dir;
        });
    }, [filtered, sort]);

    // Column totals follow the company filter — the Total row always describes what is shown.
    const monthTotals = useMemo(
        () => (matrix?.months ?? []).map((_, i) => filtered.reduce((sum, a) => sum + (a.monthlyAmounts[i] ?? 0), 0)),
        [matrix, filtered]
    );

    const toggleCompany = (code: string) => setSelectedCompanies(prev => {
        const next = new Set(prev);
        if (next.has(code)) next.delete(code); else next.add(code);
        return next;
    });

    const onSort = (key: SortKey) => setSort(s => nextSort(s, key, false));
    const isHighlighted = (y: number, m: number) => y === highlight.year && m === highlight.month;
    const numCell = 'px-3 py-1.5 text-right tabular-nums whitespace-nowrap';
    // One line, one direction — every account in this matrix inherits it. The red-negative
    // classes below deliberately keep testing the NATIVE value.
    const fmt = (amount: number) => formatEur(applySign(amount, line.signage, signMode));
    const hasGap = (matrix?.months ?? []).some(m => !m.hasData);

    /**
     * Months whose accounts do not add up to the line in the report. Only meaningful with
     * every company selected. It happens where the BAB Process App froze line amounts without
     * the account detail behind them (dfr/F&P from 2025-09) — say so rather than let the
     * reader trust a total that is quietly short.
     */
    const allCompanies = companies.length > 0 && selectedCompanies.size === companies.length;
    const outOfBalance = useMemo(() => {
        if (!matrix || !allCompanies) return [];
        return matrix.months
            .map((m, i) => ({ m, gap: monthTotals[i] - m.lineMtd }))
            .filter(x => x.m.hasData && Math.abs(x.gap) > 0.5);
    }, [matrix, monthTotals, allCompanies]);
    const gapByKey = useMemo(
        () => new Map(outOfBalance.map(x => [monthLabel(x.m.year, x.m.month), x.gap])),
        [outOfBalance]
    );

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-[96vw] max-w-[1700px] max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            {line.lineName} <span className="text-sm font-normal text-gray-400">— MTD by account</span>
                        </h2>
                        <p className="text-sm text-gray-500">
                            Line {line.lineCode} · {matrix?.months.length ?? 0} frozen months up to {monthLabel(year, month)} · full EUR
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {companies.length > 1 && (
                    <div className="flex items-center gap-2 flex-wrap px-6 py-2 border-b border-gray-100">
                        <span className="text-xs font-medium text-gray-500">Source company:</span>
                        {companies.map(code => {
                            const on = selectedCompanies.has(code);
                            return (
                                <button
                                    key={code}
                                    onClick={() => toggleCompany(code)}
                                    title={formatCompanyLabel(code)}
                                    className={`rounded px-2 py-0.5 text-xs font-medium uppercase ${on
                                        ? 'bg-blue-100 text-blue-800 ring-1 ring-blue-300 hover:bg-blue-200'
                                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                                >
                                    {code}
                                </button>
                            );
                        })}
                        <span className="ml-2 flex items-center gap-1 text-xs">
                            <button
                                onClick={() => setSelectedCompanies(new Set(companies))}
                                disabled={selectedCompanies.size === companies.length}
                                className="text-blue-600 hover:text-blue-800 disabled:text-gray-300"
                            >
                                All
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                                onClick={() => setSelectedCompanies(new Set())}
                                disabled={selectedCompanies.size === 0}
                                className="text-blue-600 hover:text-blue-800 disabled:text-gray-300"
                            >
                                None
                            </button>
                        </span>
                    </div>
                )}

                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-gray-500">
                            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading account detail…
                        </div>
                    ) : error ? (
                        <div className="m-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <SortableHeader label="Company" sortKey="company" sort={sort} onSort={onSort} />
                                    <SortableHeader label="Account" sortKey="account" sort={sort} onSort={onSort} />
                                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500 text-left">Account Name</th>
                                    {(matrix?.months ?? []).map(m => (
                                        <th
                                            key={monthLabel(m.year, m.month)}
                                            title={m.hasData ? 'Frozen (published) data' : 'No account detail was frozen for this month'}
                                            className={`sticky top-0 z-10 px-3 py-2 text-xs font-medium text-right whitespace-nowrap ${isHighlighted(m.year, m.month)
                                                ? 'bg-blue-100 text-blue-800'
                                                : 'bg-gray-50 text-gray-500'}`}
                                        >
                                            {monthLabel(m.year, m.month)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                <tr className="bg-gray-100 font-semibold">
                                    <td className="px-3 py-1.5" colSpan={3}>Total</td>
                                    {(matrix?.months ?? []).map((m, i) => {
                                        const gap = gapByKey.get(monthLabel(m.year, m.month));
                                        return (
                                            <td
                                                key={monthLabel(m.year, m.month)}
                                                title={gap !== undefined
                                                    ? `Report shows ${fmt(m.lineMtd)} — ${formatEur(Math.abs(gap))} of that month was frozen without account detail`
                                                    : undefined}
                                                className={`${numCell} ${monthTotals[i] < 0 ? 'text-red-600' : ''} ${isHighlighted(m.year, m.month) ? 'bg-blue-50' : ''}`}
                                            >
                                                {m.hasData ? fmt(monthTotals[i]) : '—'}
                                                {gap !== undefined && <span className="ml-0.5 text-amber-600">*</span>}
                                            </td>
                                        );
                                    })}
                                </tr>

                                {sorted.map(a => {
                                    const canDrill = canDrillCompany(a.companyCode);
                                    return (
                                    <tr
                                        key={`${a.companyCode}-${a.accountCode}`}
                                        className={canDrill ? 'hover:bg-blue-50 cursor-pointer' : 'hover:bg-gray-50'}
                                        onClick={canDrill ? () => setDrillAccount(a) : undefined}
                                        title={canDrill
                                            ? `Show the individual bookings of this account, YTD up to ${monthLabel(year, month)}`
                                            : `Individual bookings of ${a.companyCode.toUpperCase()} are restricted. The monthly figures here are shown in full — ask an admin for access to the bookings.`}
                                    >
                                        <td className="px-3 py-1.5 text-gray-500 uppercase" title={formatCompanyLabel(a.companyCode)}>
                                            {a.companyCode}
                                            {!canDrill && <Lock className="ml-1 inline h-3 w-3 text-gray-400" />}
                                        </td>
                                        <td className="px-3 py-1.5 font-mono whitespace-nowrap">{a.accountCode}</td>
                                        <td className="px-3 py-1.5 text-gray-700 max-w-[260px] truncate" title={a.accountName}>{a.accountName}</td>
                                        {(matrix?.months ?? []).map((m, i) => {
                                            const value = a.monthlyAmounts[i] ?? 0;
                                            const noteKey = `${a.companyCode}:${a.accountCode}|${m.year}-${m.month}`;
                                            const cmt = monthAccountCommentCounts[noteKey];
                                            const qry = monthAccountQueryCounts[noteKey];
                                            return (
                                                <td
                                                    key={monthLabel(m.year, m.month)}
                                                    className={`group relative ${numCell} ${value < 0 ? 'text-red-600' : ''} ${isHighlighted(m.year, m.month) ? 'bg-blue-50' : ''} ${!m.hasData || Math.abs(value) < 0.5 ? 'text-gray-300' : ''}`}
                                                >
                                                    {/* A note on THIS account in THIS month. `stopPropagation`
                                                        because the row click is the booking drill. */}
                                                    <span className="absolute left-0.5 top-0 flex items-center gap-0.5">
                                                        <button
                                                            type="button"
                                                            onClick={e => { e.stopPropagation(); onCommentClick(accountMonthTarget(a, m.year, m.month)); }}
                                                            title={cmt?.total
                                                                ? `${cmt.total} comment${cmt.total === 1 ? '' : 's'} on ${a.accountCode} in ${monthLabel(m.year, m.month)}`
                                                                : `Comment on ${a.accountCode} in ${monthLabel(m.year, m.month)}`}
                                                            className={`rounded p-0.5 hover:bg-black/10 ${cmt?.total ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'}`}
                                                        >
                                                            <MessageSquare size={10} className={cmt?.total ? 'text-blue-600' : ''} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={e => { e.stopPropagation(); onQueryClick(accountMonthTarget(a, m.year, m.month)); }}
                                                            title={qry?.total
                                                                ? `${qry.total} quer${qry.total === 1 ? 'y' : 'ies'} on ${a.accountCode} in ${monthLabel(m.year, m.month)}`
                                                                : `Ask about ${a.accountCode} in ${monthLabel(m.year, m.month)}`}
                                                            className={`rounded p-0.5 hover:bg-black/10 ${qry?.total ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'}`}
                                                        >
                                                            <HelpCircle
                                                                size={10}
                                                                className={qry?.total
                                                                    ? (qry.overdue > 0 ? 'text-red-600' : qry.open > 0 ? 'text-amber-600' : 'text-gray-500')
                                                                    : ''}
                                                            />
                                                        </button>
                                                    </span>
                                                    {m.hasData ? fmt(value) : '—'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    );
                                })}

                                {sorted.length === 0 && (
                                    <tr>
                                        <td colSpan={3 + (matrix?.months.length ?? 0)} className="px-3 py-8 text-center text-gray-400">
                                            {(matrix?.accounts.length ?? 0) === 0
                                                ? 'No frozen account detail for this line in the displayed months.'
                                                : 'No accounts match the selected company filter.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="px-6 py-3 border-t border-gray-200 text-xs text-gray-500 flex items-start gap-2">
                    <Info className="h-4 w-4 flex-shrink-0 mt-px" />
                    <span>
                        Each column is the published month-to-date value per account, computed as the difference
                        of frozen YTD snapshots — the Total row matches the line in the MTD report.
                        {hasGap && ' Months shown as “—” were frozen without account detail; the next frozen month carries their delta.'}
                        {outOfBalance.length > 0 && (
                            <>
                                {' '}
                                <span className="text-amber-700">
                                    Months marked <span className="font-semibold">*</span> do not add up to the report
                                    ({outOfBalance.map(x => monthLabel(x.m.year, x.m.month)).join(', ')}): those line
                                    amounts were frozen without the accounts behind them.
                                </span>
                            </>
                        )}
                        {' '}Click an account for its individual bookings. Those are LIVE data covering the whole
                        year to date up to {monthLabel(year, month)} — they do not break down per month here and
                        do not reconcile against a single frozen column.
                    </span>
                </div>
            </div>
            </div>

            {/* SIBLING of the overlay, never a child: the overlay closes on click, and React
                events bubble through the React tree even though this modal portals into
                document.body — nested, every click inside it would close the matrix.

                The YTD booking modal, unchanged. A matrix row spans every BU of its company
                (ALL_BUSINESS_UNITS) and every month up to the report period, so the bookings are
                the account's full year to date. Its own month chips narrow it to one month. */}
            {drillAccount && (
                <BabAccountTransactionModal
                    line={line}
                    account={{
                        companyCode: drillAccount.companyCode,
                        buCode: ALL_BUSINESS_UNITS,
                        accountCode: drillAccount.accountCode,
                        accountName: drillAccount.accountName,
                    }}
                    year={year}
                    month={month}
                    signMode={signMode}
                    commentedTxRefs={commentedTxRefs}
                    onCommentClick={onCommentClick}
                    queriedTxRefs={queriedTxRefs}
                    onQueryClick={onQueryClick}
                    onClose={() => setDrillAccount(null)}
                />
            )}
        </>
    );
}
